import PropTypes from 'prop-types'
import React, { useEffect, useCallback, useState } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, fromJS } from 'immutable'
import _get from 'lodash/get'
import { connect } from 'react-redux'
import { useMutation } from '@apollo/client'
import VideoPlayerUpNext from '../VideoPlayerUpNext/index'
import VideoPlayerBack from '../VideoPlayerBack/index'
import VideoCommentsToggle from '../VideoCommentsToggle/index'
import { getBoundActions } from '../../actions/index'
import { USER_STATE_CREATE_OR_UPDATE } from '../../services/detail/query'
import { createTitleGroupId } from '../../services/content/index'
import { USER_STATE_PROGRESS_ITEM_TYPE_VIDEO, COURSE_STATE_COMPLETE } from '../../services/detail/index'
import { TYPE_CONTENT_VIDEO, TYPE_CONTENT_SERIES, TYPE_CONTENT_COURSE } from '../../services/content-type/index'
import { TYPE_PREVIEW } from '../../services/video/index'
import { TYPE_LOGIN, TYPE_SHARE_V2_SHARE } from '../../services/dialog/index'
import Button, { TYPES } from '../Button/index'
import { ICON_TYPES } from '../Icon.v2/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../ButtonSignUp/index'
import GaiaLogo, { TYPE_TEAL as GAIA_LOGO_TYPE_TEAL } from '../GaiaLogo/index'
import VideoPlayerMeta from '../VideoPlayerMeta/index'
import VideoPlayerEndedPreviewEntitled from '../VideoPlayerEndedPreviewEntitled/index'
import { URL_HOME } from '../../services/url/constants'
import { getEmailCustomerServiceUrl } from '../../services/url/index'
import { getPrimary } from '../../services/languages/index'
import Link from '../Link/index'
import { TARGET_BLANK } from '../Link/constants'
import { USER_ACCOUNT_PAUSED } from '../../services/user-account/index'
import { CONTEXT_TYPE_VIDEO_END_STATE } from '../../services/upstream-context/index'
import { NotificationsFollowButton, BUTTON_TYPES } from '../NotificationsFollowButton/index'
import { SUBSCRIPTION_TYPES } from '../../services/notifications/index'
import { isEntitled } from '../../services/subscription/index'
import TitleVideoLink, { TYPE as VIDEO_LINK_TYPE } from '../TitleVideoLink/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { SHARE_INITIATED_GA4_EVENT } from '../../services/event-tracking/index'
import ContentUpdater from '../ContentUpdater/index'
import { get as getConfig } from '../../config/index'
import { VOTE_SIZE_MEDIUM } from '../../services/recommend/index'
import VideoPlayerVote from '../VideoPlayerVote/index'

const config = getConfig()
const tilesV2GraphqlActive = !!_get(config, ['features', 'tilesV2Graphql'])
const courseDetailPageActive = !!_get(config, ['features', 'courseDetailPage'])

//
// cached class names
// we're doing this because of old react components
// built ages ago that use arrays as class names.
// arrays cause re-rendering when supplied in render()
// since these classnames never change, we're hoisting them
// to the top of this file and referencing the classes throughout
const CLASS_NAMES = {
  SHARE: ['button--ghost', 'button--with-icon', 'button--stacked', 'jumbotron-episode__action-button'],
  SIGNUP: ['button--primary video-player-ended__sign-up-button'],
  LOGIN: ['button--ghost video-player-ended__login-button'],
  VIDEO_PLAYER_BACK: ['video-player-ended__back'],
  META: ['video-player-ended__video-player-meta'],
  LOGO: ['video-player-ended__gaia-logo'],
  ICON_SHARE: ['icon--share'],
}

function renderFeatureEndState (props, onShare) {
  const {
    staticText, isShareable, video, auth, primaryCourse,
  } = props
  const seriesId = video.getIn(['data', 'seriesId'])
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()))
  const isLoggedIn = getAuthIsLoggedIn(auth)

  let ga4EventData = fromJS({
    content_id: video.getIn(['data', 'id']),
    content_type: video.getIn(['data', 'contentType']),
    content_name: video.getIn(['data', 'title']),
    ui_location: 'End Screen',
  })

  if (seriesId) {
    ga4EventData = ga4EventData.mergeDeep({
      parent_series_id: seriesId,
      parent_series_name: video.getIn(['data', 'seriesTitle']),
    })
  }

  return (
    <div className="video-player-ended__meta">
      <div className="video-player-ended__you-finished">
        {staticText.getIn(['data', 'youJustFinished'])}
      </div>
      <TitleVideoLink
        type={VIDEO_LINK_TYPE.VIDEO}
        linkClass={'video-player-ended__title'}
      />
      <TitleVideoLink
        type={primaryCourse ? VIDEO_LINK_TYPE.COURSE : VIDEO_LINK_TYPE.SERIES}
        linkClass={'video-player-ended__title'}
        primaryCourse={primaryCourse}
      />
      {isLoggedIn && isEntitled
        && <div className="video-player-ended__vote">
          <p className="video-player-ended__vote-title">
            {staticText.getIn(['data', 'voteTitle'])}
          </p>
          <VideoPlayerVote
            auth={auth}
            contentId={video.getIn(['data', 'id'])}
            size={VOTE_SIZE_MEDIUM}
            heartText={staticText.getIn(['data', 'voteLoveLabel'])}
            thumbUpText={staticText.getIn(['data', 'voteUpLabel'])}
            thumbDownText={staticText.getIn(['data', 'voteDownLabel'])}
            ga4EventData={ga4EventData}
          />
          <p className="video-player-ended__vote-hint">
            {staticText.getIn(['data', 'voteHint'])}
          </p>
        </div>
      }
      {seriesId && isLoggedIn && !primaryCourse ? (
        <NotificationsFollowButton
          subscriptionType={SUBSCRIPTION_TYPES.SERIES}
          type={BUTTON_TYPES.LINK}
          contentId={seriesId}
          uiLocation={'End Screen'}
        />
      ) : null}
      {isShareable && userIsEntitled && isLoggedIn
        ? <Button
            type={TYPES.ICON}
            icon={ICON_TYPES.SHARE}
            onClick={onShare}
            className="video-player-ended__action-button button--with-icon"
            stacked
            inverted
          >
            {staticText.getIn(['data', 'share'])}
          </Button>
        : null
      }
    </div>
  )
}

function renderPreviewEndState (props, onLogin) {
  const {
    userAccount, staticText, user, auth,
  } = props

  const authToken = auth.get('jwt')
  const subscriptions = auth.get('subscriptions', List())
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const holdMessage = staticText.getIn(['data', 'membershipIsPaused'])
  const accountStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'])
  const unentitledMessage = authToken ? staticText.getIn(['data', 'becomeAMemberOrLoginToContinueWatching'])
    : staticText.getIn(['data', 'becomeAMemberToContinueWatching'])

  // paused account end state
  if (authToken && accountStatus === USER_ACCOUNT_PAUSED) {
    return (
      <div className="video-player-ended__preview">
        <GaiaLogo className={CLASS_NAMES.LOGO} type={GAIA_LOGO_TYPE_TEAL} />
        <span className="video-player-ended__account-paused">{holdMessage}</span>
        <Button
          type={TYPES.PRIMARY}
          url={getEmailCustomerServiceUrl(userLanguage)}
          target={TARGET_BLANK}
          className="video-player-ended__sign-up-button"
        >
          {staticText.getIn(['data', 'contact'])}
        </Button>
        <Link
          to={URL_HOME}
          className="video-player-ended__home-link"
        >{staticText.getIn(['data', 'gaiaHomeLink'])}
        </Link>
      </div>
    )
  }
  // unentitled end state
  if (!subscriptions.size) {
    return (
      <div className="video-player-ended__preview">
        <GaiaLogo className={CLASS_NAMES.LOGO} type={GAIA_LOGO_TYPE_TEAL} />
        <span className="video-player-ended__sign-up-copy">{unentitledMessage}</span>
        <ButtonSignUp
          text={staticText.getIn(['data', 'signUp'])}
          type={BUTTON_SIGN_UP_TYPE_BUTTON}
          buttonClass={CLASS_NAMES.SIGNUP}
          scrollToTop
        />
        {!authToken ? (
          <Button
            type={TYPES.GHOST}
            onClick={onLogin}
            className="video-player-ended__login-button"
          >
            {staticText.getIn(['data', 'logIn'])}
          </Button>
        ) : null}
      </div>
    )
  }
  // default end state
  return (
    <VideoPlayerEndedPreviewEntitled
      location={props.location}
      video={props.video}
      language={getPrimary(user.getIn(['data', 'language']))}
    />
  )
}

function VideoPlayerEnded (props) {
  const {
    refreshComments,
    location,
    comments,
    history,
    video,
    auth,
    type,
    user,
    primaryCourse,
  } = props

  const [showUpNext, setShowUpNext] = useState(false)

  const enableTimer = video.getIn(['endState', 'timerVisible'], null) !== false
  const previousVideo = video.get('data')
  const subscriptions = auth.get('subscriptions', List())
  const videoId = video.getIn(['data', 'id'], null)
  const authJwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))

  const [userStateCreateOrUpdate] = useMutation(USER_STATE_CREATE_OR_UPDATE, {
    variables: {
      progressItemType: USER_STATE_PROGRESS_ITEM_TYPE_VIDEO,
      state: COURSE_STATE_COMPLETE,
    },
    context: {
      jwt: authJwt,
    },
  })

  //
  // componentWillUnmount hook
  // remove the endstate timer from redux
  useEffect(() => () => {
    const { setVideoEndStateTimerVisible } = props
    setVideoEndStateTimerVisible(null)
  })

  //
  // onCommentsWillShow event listener
  // stops the endstate timer from incrementing
  const onCommentsWillShow = useCallback(() => {
    const { setVideoEndStateTimerVisible } = props
    setVideoEndStateTimerVisible(false)
  }, [])

  //
  // onLogin event listener
  // renders the login modal
  const onLogin = useCallback((e) => {
    e.preventDefault()
    const { setOverlayDialogVisible } = props
    setOverlayDialogVisible(TYPE_LOGIN)
  }, [])

  //
  // onShare event listener
  // renders the share modal
  const onShare = useCallback(() => {
    const {
      setVideoEndStateTimerVisible,
      setEventDefault,
      renderModal,
      clearShare,
    } = props
    setVideoEndStateTimerVisible(false)
    renderModal(TYPE_SHARE_V2_SHARE, {
      contentId: video.getIn(['data', 'id']),
      contentUrl: video.getIn(['data', 'path']),
      contentType: video.getIn(['data', 'contentType']),
      parentSeriesId: video.getIn(['data', 'seriesId']),
      parentSeriesName: video.getIn(['data', 'seriesTitle']),
      title: video.getIn(['data', 'title']),
      uiLocation: 'End Screen',
      onDismiss: () => clearShare(),
    })
    let ga4EventData = {
      content_name: video.getIn(['data', 'title']),
      content_id: video.getIn(['data', 'id']),
      content_type: video.getIn(['data', 'contentType']),
    }
    if (video.getIn(['data', 'seriesId'])) {
      ga4EventData = {
        ...ga4EventData,
        parent_series_name: video.getIn(['data', 'seriesTitle']),
        parent_series_id: video.getIn(['data', 'seriesId']),
      }
    }
    setEventDefault(SHARE_INITIATED_GA4_EVENT.mergeDeep({
      ...ga4EventData,
      ui_location: 'End Screen',
    }))
  }, [video])

  const markCourseStepComplete = useCallback(async (primaryCourseId) => {
    await userStateCreateOrUpdate({
      variables: {
        progressItemId: createTitleGroupId(videoId, TYPE_CONTENT_VIDEO),
        parentContentTitleGroupId: createTitleGroupId(primaryCourseId, TYPE_CONTENT_COURSE),
      },
      context: {
        jwt: authJwt,
      },
    })
    setShowUpNext(true)
  }, [userStateCreateOrUpdate, authJwt])

  useEffect(() => {
    if (authJwt && type !== TYPE_PREVIEW) {
      if (primaryCourse && courseDetailPageActive) {
        markCourseStepComplete(primaryCourse.get('contentId'))
      } else {
        setShowUpNext(true)
      }
    }
  }, [authJwt, type, primaryCourse, markCourseStepComplete])

  return (
    <div className="video-player-ended">
      <VideoPlayerBack
        className={CLASS_NAMES.VIDEO_PLAYER_BACK}
        location={location}
        history={history}
        visible
      />
      {type === TYPE_PREVIEW && !subscriptions.size
        && video.getIn(['data', 'id']) != null
        && video.getIn(['data', 'title']) != null ? (
          <VideoPlayerMeta
            id={video.getIn(['data', 'id'])}
            title={video.getIn(['data', 'title'])}
            geoAvailibility={video.getIn(
              ['data', 'feature', 'georestrictions', 'availability'],
              '',
            )}
            duration={video.getIn(['data', 'feature', 'duration'])}
            className={CLASS_NAMES.META}
            moreVisible={false}
            votesVisible={false}
            visible
            playerPause={() => {}}
            playerPlay={() => {}}
            playerPlayOrPause={() => {}}
            isPlaying={() => false}
          />
        ) : null}
      {type === TYPE_PREVIEW
        ? renderPreviewEndState(props, onLogin)
        : renderFeatureEndState(props, onShare)}
      <VideoCommentsToggle
        commentsCount={video.getIn(['data', 'commentTotalCount'], 0)}
        className={['video-player-ended__video-comments-toggle']}
        contextType={CONTEXT_TYPE_VIDEO_END_STATE}
        onCommentsWillShow={onCommentsWillShow}
        refreshComments={refreshComments}
        comments={comments}
        video={video}
        auth={auth}
        visible
      />
      {showUpNext ? (
        <div className="video-player-ended__up-next">
          <VideoPlayerUpNext
            setVideoPlayerEnded={props.setVideoPlayerEnded}
            previousVideo={previousVideo}
            getTilesData={props.getTilesData}
            deleteTiles={props.deleteTiles}
            enableTimer={enableTimer}
            location={props.location}
            history={props.history}
            tiles={props.tiles}
            auth={props.auth}
            id={videoId}
            primaryCourse={primaryCourse}
          />
        </div>
      ) : null}
      {
        tilesV2GraphqlActive && authJwt && type !== TYPE_PREVIEW
          ? <ContentUpdater
              contentId={video.getIn(['data', 'id'])}
              contentType={TYPE_CONTENT_VIDEO}
              secondaryContentId={video.getIn(['data', 'seriesId'])}
              secondaryContentType={TYPE_CONTENT_SERIES}
              authJwt={authJwt}
              userLanguage={userLanguage}
              runOnMount
            />
          : null
        }
    </div>
  )
}

VideoPlayerEnded.propTypes = {
  setVideoPlayerEnded: PropTypes.func.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  comments: ImmutablePropTypes.map.isRequired,
  video: ImmutablePropTypes.map.isRequired,
  tiles: ImmutablePropTypes.map.isRequired,
  getTilesData: PropTypes.func.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  deleteTiles: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  primaryCourse: ImmutablePropTypes.map,
}

export default connect(
  (state) => ({
    staticText: state.staticText.getIn(['data', 'videoPlayerEnded']),
    isShareable: state.media.getIn(['data', 'isShareable'], false),
    userAccount: state.userAccount,
    comments: state.comments,
    user: state.user,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setVideoEndStateTimerVisible: actions.video.setVideoEndStateTimerVisible,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      refreshComments: actions.comments.refreshComments,
      renderModal: actions.dialog.renderModal,
      clearShare: actions.share.clearShare,
      setEventDefault: actions.eventTracking.setEventDefault,
    }
  },
)(VideoPlayerEnded)
